import type { BrainSignalSemantic } from './brainSignals';
export enum BrainCategory {
  DECISION = 'decision',
  OWNER = 'owner',
  DEADLINE = 'deadline',
  BLOCKER = 'blocker',
}

export enum BrainPredicate {
  DECIDED_TO = 'DECIDED_TO',
  APPROVED = 'APPROVED',
  OWNS = 'OWNS',
  LEADS = 'LEADS',
  HAS_DEADLINE = 'HAS_DEADLINE',
  HAS_LAUNCH_DATE = 'HAS_LAUNCH_DATE',
  BLOCKED_BY = 'BLOCKED_BY',
  IMPACTS = 'IMPACTS',
}

export const BRAIN_PREDICATES_BY_CATEGORY: Record<
  BrainCategory,
  readonly BrainPredicate[]
> = {
  [BrainCategory.DECISION]: [
    BrainPredicate.DECIDED_TO,
    BrainPredicate.APPROVED,
  ],
  [BrainCategory.OWNER]: [BrainPredicate.OWNS, BrainPredicate.LEADS],
  [BrainCategory.DEADLINE]: [
    BrainPredicate.HAS_DEADLINE,
    BrainPredicate.HAS_LAUNCH_DATE,
  ],
  [BrainCategory.BLOCKER]: [BrainPredicate.BLOCKED_BY, BrainPredicate.IMPACTS],
};

export const BRAIN_CATEGORY_BY_PREDICATE: Readonly<
  Record<BrainPredicate, BrainCategory>
> = Object.fromEntries(
  Object.entries(BRAIN_PREDICATES_BY_CATEGORY).flatMap(
    ([category, predicates]) =>
      predicates.map((predicate) => [predicate, category]),
  ),
) as Record<BrainPredicate, BrainCategory>;

export enum BrainModality {
  COMMITTED = 'committed',
  ESTIMATED = 'estimated',
  PROPOSED = 'proposed',
}

export enum BrainEntityType {
  PERSON = 'PERSON',
  PROJECT = 'PROJECT',
  TOPIC = 'TOPIC',
  SYSTEM = 'SYSTEM',
  TEAM = 'TEAM',
  CUSTOMER = 'CUSTOMER',
}

const INTERCHANGEABLE_ENTITY_TYPES: ReadonlySet<BrainEntityType> = new Set([
  BrainEntityType.CUSTOMER,
  BrainEntityType.TOPIC,
]);

export const areBrainEntityTypesCompatible = (
  a: BrainEntityType,
  b: BrainEntityType,
): boolean =>
  a === b ||
  (INTERCHANGEABLE_ENTITY_TYPES.has(a) && INTERCHANGEABLE_ENTITY_TYPES.has(b));

const SIGNAL_TOPIC_STAND_IN_TYPES: ReadonlySet<BrainEntityType> = new Set([
  BrainEntityType.CUSTOMER,
  BrainEntityType.PROJECT,
  BrainEntityType.TOPIC,
]);

// Discovery falls back to TOPIC when unsure, so a strict signal scope would fail silently.
export const brainSignalEntityTypeSatisfies = (
  required: BrainEntityType,
  actual: BrainEntityType,
): boolean =>
  required === actual ||
  (SIGNAL_TOPIC_STAND_IN_TYPES.has(required) &&
    SIGNAL_TOPIC_STAND_IN_TYPES.has(actual) &&
    (required === BrainEntityType.TOPIC || actual === BrainEntityType.TOPIC));

export const compatibleBrainEntityTypes = (
  type: BrainEntityType,
): BrainEntityType[] =>
  Object.values(BrainEntityType).filter((candidate) =>
    areBrainEntityTypesCompatible(type, candidate),
  );

export enum BrainExtractionState {
  CANDIDATE = 'candidate',
  EVIDENCE_VALIDATED = 'evidence_validated',
  INVALID = 'invalid',
  UNSUPPORTED = 'unsupported',
}

export enum BrainReviewState {
  NOT_REQUIRED = 'not_required',
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum BrainKnowledgeState {
  ACTIVE = 'active',
  DISPUTED = 'disputed',
  SUPERSEDED = 'superseded',
  RETRACTED = 'retracted',
}

export enum BrainPublicationState {
  UNPUBLISHED = 'unpublished',
  SCOPED = 'scoped',
  REVOKED = 'revoked',
}

export enum BrainClassification {
  INTERNAL = 'internal',
  RESTRICTED = 'restricted',
}

export enum BrainSourceType {
  SLACK = 'slack',
  EMAIL = 'email',
}

export enum BrainReviewMode {
  AUTO_PUBLISH_CONFIDENT = 'auto_publish_confident',
  REVIEW_ALL = 'review_all',
}

export enum BrainConfidenceSignal {
  JEV_GROUNDED = 'jev_grounded',
  MODEL_GROUNDED = 'model_grounded',
  DETERMINISTIC_GROUNDED = 'deterministic_grounded',
  SPANS_EXACT = 'spans_exact',
  NO_CONFLICT = 'no_conflict',
  SUBJECT_DECLARED = 'subject_declared',
  SUBJECT_KNOWN = 'subject_known',
  OWNER_IDENTITY = 'owner_identity',
}

export enum BrainEntityResolvedBy {
  IDENTITY_KEY = 'identity_key',
  DECLARED_ALIAS = 'declared_alias',
  EXACT_NAME = 'exact_name',
  JEV_MATCH = 'jev_match',
  CREATED = 'created',
}

export enum BrainAcceptedBy {
  CONFIDENCE = 'confidence',
  REVIEWER = 'reviewer',
}

export enum BrainBlueprintStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  SUPERSEDED = 'superseded',
}

export enum BrainReviewDecision {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum BrainSkipReason {
  NOT_IN_BLUEPRINT = 'NOT_IN_BLUEPRINT',
  DM = 'DM',
  BOT = 'BOT',
  BULK_EMAIL = 'BULK_EMAIL',
  TOO_SHORT = 'TOO_SHORT',
  HISTORICAL = 'HISTORICAL',
  BUDGET = 'BUDGET',
  JEV_IRRELEVANT = 'JEV_IRRELEVANT',
  DUPLICATE_JOB = 'DUPLICATE_JOB',
  EXTRACTION_FAILED = 'EXTRACTION_FAILED',
  NO_HOLDERS = 'NO_HOLDERS',
  WORKSPACE_NOT_LINKED = 'WORKSPACE_NOT_LINKED',
  PRIVATE_SOURCE_NO_REVIEWER = 'PRIVATE_SOURCE_NO_REVIEWER',
}

export enum BrainEntityOrigin {
  DECLARED = 'declared',
  DISCOVERED = 'discovered',
}

export enum BrainEntityStatus {
  ACTIVE = 'active',
  NEEDS_CONFIRMATION = 'needs_confirmation',
  MERGED = 'merged',
}

/** Where a PERSON stands against the team: seen in mail, invited, joined, or left. */
export enum BrainPersonMembership {
  SUGGESTED = 'suggested',
  INVITED = 'invited',
  MEMBER = 'member',
  FORMER = 'former',
}

/** Why discovery took an address for a coworker. */
export enum BrainCoworkerEvidence {
  COMPANY_DOMAIN = 'company_domain',
  SIGNATURE_TITLE = 'signature_title',
  BILATERAL_EXCHANGE = 'bilateral_exchange',
  CC_WITH_INTERNALS = 'cc_with_internals',
}

export interface BrainCoworkerStats {
  sent: number;
  received: number;
  threads: number;
  firstSeen: string;
  lastSeen: string;
}

export enum BrainEdgeRelation {
  WORKS_ON = 'works_on',
  OWNS = 'owns',
  PART_OF = 'part_of',
  LIVES_IN = 'lives_in',
  /** PERSON to PERSON; only ever suggested by discovery, never derived from a fact. */
  REPORTS_TO = 'reports_to',
}

export enum BrainEdgeOrigin {
  FACT = 'fact',
  DECLARED = 'declared',
  IDENTITY = 'identity',
  SIGNAL = 'signal',
}

export enum BrainEdgeStatus {
  ACTIVE = 'active',
  SUPERSEDED = 'superseded',
  DISPUTED = 'disputed',
  NEEDS_CONFIRMATION = 'needs_confirmation',
}

export enum BrainEdgeDirection {
  OUT = 'out',
  IN = 'in',
}

export enum BrainBackfillStatus {
  ESTIMATED = 'estimated',
  RUNNING = 'running',
  PAUSED = 'paused',
  DONE = 'done',
  FAILED = 'failed',
}

export enum BrainBackfillPauseReason {
  CAP_REACHED = 'cap_reached',
  PAUSED_BY_USER = 'paused_by_user',
  ENQUEUE_FAILED = 'enqueue_failed',
}

export enum BrainObjectKind {
  TEXT = 'text',
  DATE = 'date',
  ENTITY = 'entity',
  BOOLEAN = 'boolean',
  MONEY = 'money',
}

export enum BrainEventKind {
  FACT_CREATED = 'FACT_CREATED',
  FACT_EVIDENCE_ADDED = 'FACT_EVIDENCE_ADDED',
  FACT_EVIDENCE_REMOVED = 'FACT_EVIDENCE_REMOVED',
  FACT_TEXT_REVISED = 'FACT_TEXT_REVISED',
  FACT_SUPERSEDED = 'FACT_SUPERSEDED',
  FACT_SUPERSEDES = 'FACT_SUPERSEDES',
  FACT_DISPUTE_OPENED = 'FACT_DISPUTE_OPENED',
  FACT_DISPUTE_RELEASED = 'FACT_DISPUTE_RELEASED',
  FACT_DISPUTE_SETTLED = 'FACT_DISPUTE_SETTLED',
  FACT_APPROVED = 'FACT_APPROVED',
  FACT_REJECTED = 'FACT_REJECTED',
  FACT_RETRACTED = 'FACT_RETRACTED',
  FACT_HISTORICAL_ADDED = 'FACT_HISTORICAL_ADDED',
  FACT_SUBJECT_MOVED = 'FACT_SUBJECT_MOVED',
  FACT_MIRRORED = 'FACT_MIRRORED',
  FACT_REMOVED = 'FACT_REMOVED',
  ENTITY_CREATED = 'ENTITY_CREATED',
  ENTITY_CONFIRMED = 'ENTITY_CONFIRMED',
  ENTITY_REVISED = 'ENTITY_REVISED',
  ENTITY_REPARENTED = 'ENTITY_REPARENTED',
  ENTITY_MERGED = 'ENTITY_MERGED',
  ENTITY_PRUNED = 'ENTITY_PRUNED',
}

export enum BrainEventOrigin {
  LIVE = 'LIVE',
  BACKFILL = 'BACKFILL',
  DEFERRED = 'DEFERRED',
  REVIEW = 'REVIEW',
  MERGE = 'MERGE',
  REPLAY = 'REPLAY',
}

export enum BrainEventActor {
  SYSTEM = 'SYSTEM',
  REVIEWER = 'REVIEWER',
  ADMIN = 'ADMIN',
}

/** Written on the REV item only; enums and an id, readable. */
export interface BrainRevisionEvent {
  kind: BrainEventKind;
  origin: BrainEventOrigin;
  actor: BrainEventActor;
  causeAssertionId?: string;
}

/** Enums, a time and a counter, readable: the engine branches on them. */
export interface BrainEntityEventStamp {
  kind: BrainEventKind;
  at: string;
  seq: number;
}

export enum BrainTraceStage {
  INGESTED = 'INGESTED',
  GROUPED = 'GROUPED',
  GATED = 'GATED',
  EXTRACTED = 'EXTRACTED',
  VALIDATED = 'VALIDATED',
  RESOLVED = 'RESOLVED',
  RECONCILED = 'RECONCILED',
  REVIEW_OPENED = 'REVIEW_OPENED',
  REVIEW_DECIDED = 'REVIEW_DECIDED',
  COMMITTED = 'COMMITTED',
  PROJECTED = 'PROJECTED',
  RETRACTED = 'RETRACTED',
}

export enum BrainBlueprintItemOrigin {
  DECLARED = 'declared',
  DISCOVERY = 'discovery',
}

/** Why discovery proposed a name: the mention kinds it was seen in, plus the draft's own checks. */
export enum BrainDiscoverySignal {
  SUBJECT = 'subject',
  BODY = 'body',
  QUOTED = 'quoted',
  INVITE = 'invite',
  CODE = 'code',
  HIERARCHY_CONFLICT = 'hierarchy_conflict',
  CROSS_HOLDER = 'cross_holder',
  PUBLIC_CHANNEL = 'public_channel',
  /** Named after a picked public Slack channel. */
  CHANNEL_NAME = 'channel_name',
}

/** Enums and a number only, so it stays readable on the sealed blueprint row. */
export interface BrainBlueprintItemProvenance {
  origin: BrainBlueprintItemOrigin;
  /** 0 to 1. */
  confidence: number;
  signals: BrainDiscoverySignal[];
}

export interface BrainBlueprintArea {
  key: string;
  name: string;
  /** Null: activation creates a "Company Brain · <name>" TEAM space for the area. */
  spaceId: string | null;
  provenance?: BrainBlueprintItemProvenance;
  /** Team members placed in the area; readable: activation keys each PERSON node by them. */
  memberUserIds?: string[];
}

export interface BrainBlueprintEntityAlias {
  entityId: string;
  type: BrainEntityType;
  name: string;
  aliases: string[];
  areaKeys: string[];
  parentEntityId?: string;
  provenance?: BrainBlueprintItemProvenance;
}

/** displayName, title, variants, emailDomain and email are sealed on the blueprint row; emailHmac stays comparable. */
export interface BrainBlueprintSuggestedPerson {
  /** Stable id of the suggestion inside the draft. */
  personRef: string;
  /** hmacEmail digest of the address, as in the `email:<hmac>` person key. */
  emailHmac: string;
  displayName: string;
  nameVariants?: string[];
  title: string | null;
  titleVariants?: string[];
  emailDomain: string;
  /** Clear address, sealed; only the team admin reads it, on demand, to open an invite. */
  email?: string;
  /** 1-2 sentences on who they are and what they do; sealed, shown as an automatic summary. */
  summary?: string | null;
  /** personRef of another suggestion; becomes a suggested reports_to edge on activation. */
  reportsToRef?: string;
  /** 0 to 1. */
  reportsToConfidence?: number;
  /** Short excerpt behind reportsToRef; sealed. */
  reportsToEvidence?: string;
  areaKeys: string[];
  evidence: BrainCoworkerEvidence[];
  /** 0 to 1. */
  confidence: number;
  provenance: BrainBlueprintItemProvenance;
  stats?: BrainCoworkerStats;
  /** The admin kept this suggestion explicitly. */
  confirmed?: boolean;
}

export enum BrainSuggestedSignalStatus {
  SUGGESTED = 'SUGGESTED',
  KEPT = 'KEPT',
  DISCARDED = 'DISCARDED',
}

export interface BrainSignalTemplateTexts {
  name: string;
  description?: string;
  /** The translated semantic block the admin saw; handed to the activation as received. */
  semantic?: Omit<BrainSignalSemantic, 'depth'> & {
    depth?: BrainSignalSemantic['depth'];
  };
}

export interface BrainBlueprintSuggestedSignal {
  templateId: string;
  status: BrainSuggestedSignalStatus;
  /** Client-translated copy the admin saw when keeping it; sealed at rest, set only while KEPT. */
  texts?: BrainSignalTemplateTexts;
  reason: {
    entityCount: number;
    factCount: number;
    entityTypes: BrainEntityType[];
  };
}

export interface BrainBlueprintSources {
  slackChannelIds: string[];
  emailEnabled: boolean;
}

export interface BrainBlueprintProcessing {
  debounceMinutes: number;
  /** BRAIN_UNLIMITED_DAILY_EXTRACTION_CAP (0) means no daily limit. */
  dailyExtractionCap: number;
  reviewMode: BrainReviewMode;
  /** Minimum fact confidence to publish without review; null keeps the legacy rule (INTERNAL and confident only). */
  autoAcceptConfidence: number | null;
}

export interface BrainBlueprint {
  teamId: string;
  version: number;
  status: BrainBlueprintStatus;
  sources: BrainBlueprintSources;
  categories: BrainCategory[];
  areas: BrainBlueprintArea[];
  entityAliases: BrainBlueprintEntityAlias[];
  /** Coworkers discovery saw; activation turns each kept one into a PERSON. Absent on older drafts. */
  suggestedPeople?: BrainBlueprintSuggestedPerson[];
  /** Signal templates discovery found evidence for; kept ones become definitions on activation. Ids, status and counts are clear; `texts` is sealed. */
  suggestedSignals?: BrainBlueprintSuggestedSignal[];
  /** ISO time the continuous refresh last ran over an active blueprint; clear, a timestamp only. */
  suggestedSignalsRefreshedAt?: string;
  reviewerUserIds: string[];
  reviewTaskGroupId: string | null;
  processing: BrainBlueprintProcessing;
  createdBy: string;
  createdAt: string;
  activatedBy?: string;
  activatedAt?: string;
  changeSummary?: string;
  /** The discovery run that pre-filled this draft; readable, an id. */
  discoveryRunId?: string;
}

export interface BrainObject {
  kind: BrainObjectKind;
  value: string;
  entityId?: string;
  currency?: string;
}

export interface BrainEvidenceRef {
  evidenceId: string;
  sourceType: BrainSourceType;
  channel?: string;
  messageId: string;
  threadKey: string;
  contentHash: string;
  collectedAt: string;
}

export interface BrainProvenance {
  extractorModelId: string;
  promptVersion: string;
  pipelineVersion: string;
  blueprintVersion: number;
  jobHash: string;
  reviewCardId?: string;
  decidedBy?: string;
  acceptedBy?: BrainAcceptedBy;
  confidence?: BrainProvenanceConfidence;
}

export interface BrainProvenanceConfidence {
  score: number;
  /** null when the blueprint still uses the legacy publication rule. */
  threshold: number | null;
  signals: BrainConfidenceSignal[];
}

export interface BrainAssertion {
  teamId: string;
  assertionId: string;
  revision: number;
  subjectEntityId: string;
  subjectName: string;
  category: BrainCategory;
  predicate: BrainPredicate;
  object: BrainObject;
  statement: string;
  modality: BrainModality;
  effectiveAt: string;
  validFrom?: string;
  validTo?: string;
  areaKeys: string[];
  spaceId: string | null;
  classification: BrainClassification;
  extractionState: BrainExtractionState;
  reviewState: BrainReviewState;
  knowledgeState: BrainKnowledgeState;
  publicationState: BrainPublicationState;
  evidence: BrainEvidenceRef[];
  holderUserIds: string[];
  /** `email:<hmac>` / `clerk:<digest>` keys of everyone who was on the thread; sealed with holderUserIds. */
  holderPersonKeys?: string[];
  supersedesAssertionId?: string;
  supersededByAssertionId?: string;
  disputedWithAssertionIds?: string[];
  /** Internal assertion built from public evidence that mirrors a restricted one. */
  mirrorOfAssertionId?: string;
  mirroredByAssertionId?: string;
  /** PERSON entities that authored the evidence; ids only, read by the edge derivation. */
  authorEntityIds?: string[];
  /** ISO date (YYYY-MM-DD) of a DEADLINE fact whose object is a date. Readable: the signals sweep queries it by key (engine branches on it); it is a date the message already states, never free text. */
  dueAt?: string;
  provenance: BrainProvenance;
  createdAt: string;
  updatedAt: string;
}

export interface BrainEntity {
  teamId: string;
  entityId: string;
  type: BrainEntityType;
  canonicalName: string;
  aliases: string[];
  areaKeys: string[];
  personId?: string;
  projectionPageId?: string;
  origin: BrainEntityOrigin;
  status: BrainEntityStatus;
  parentEntityId?: string;
  mergedIntoEntityId?: string;
  /** spaceId -> pageId of the entity's projection page in each area space. */
  projectionPageIds?: Record<string, string>;
  /** Sealed: the app user and Slack workspaces this PERSON is known by. */
  identities?: BrainEntityIdentities;
  isTeammate?: boolean;
  isFormerMember?: boolean;
  /** PERSON only. Readable: the engine branches on it. */
  membership?: BrainPersonMembership;
  /** PERSON profile, sealed with the names. */
  nameVariants?: string[];
  title?: string;
  titleVariants?: string[];
  /** Sealed. */
  summary?: string;
  /** Excerpt behind a suggested reports_to edge; sealed. */
  reportsToEvidence?: string;
  /** Enums only, readable. */
  coworkerEvidence?: BrainCoworkerEvidence[];
  /** Counts and timestamps from the collection, readable. */
  stats?: BrainCoworkerStats;
  /** Last knowledge change; readable (kind, time, per-entity sequence, no content): the signals consumer branches on it. */
  lastEvent?: BrainEntityEventStamp;
  createdAt: string;
  updatedAt: string;
}

export interface BrainEntityIdentities {
  userId?: string;
  workspaceIds?: string[];
}

/** channelId replaces toEntityId on lives_in, the only relation that points at a channel. */
export interface BrainEdge {
  teamId: string;
  fromEntityId: string;
  relation: BrainEdgeRelation;
  toEntityId?: string;
  channelId?: string;
  origin: BrainEdgeOrigin;
  status: BrainEdgeStatus;
  firstSeen: string;
  lastSeen: string;
  count: number;
  sourceAssertionIds: string[];
  declaredByEntityId?: string;
  projectable: boolean;
  /** 0 to 1; set on a reports_to suggestion, readable. */
  confidence?: number;
  /** Set when an admin confirmed a suggested edge; a confirmed edge outlives its fact sources. */
  confirmedAt?: string;
  updatedAt: string;
}

export interface BrainSkipCounters {
  teamId: string;
  /** YYYY-MM-DD (UTC). */
  day: string;
  byReason: Partial<Record<BrainSkipReason, number>>;
}

export interface BrainBackfillEstimatedCost {
  extractions: number;
}

export interface BrainBackfillPlan {
  teamId: string;
  planId: string;
  blueprintVersion: number;
  sourceType: BrainSourceType;
  channel?: string;
  fromDate: string;
  toDate: string;
  estimatedThreads: number;
  estimatedCost?: BrainBackfillEstimatedCost;
  estimateTruncated?: boolean;
  processedThreads: number;
  skippedThreads: number;
  extractedThreads: number;
  cap: number;
  status: BrainBackfillStatus;
  pausedReason?: BrainBackfillPauseReason;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface BrainBackfillCursor {
  teamId: string;
  planId: string;
  holderUserId: string;
  /** Opaque LastEvaluatedKey of the holder's UserCommunicationsByCreatedAt page. */
  exclusiveStartKey?: Record<string, unknown>;
  done: boolean;
  updatedAt: string;
}

export interface BrainTraceEvent {
  stage: BrainTraceStage;
  at: string;
  code: string;
  durationMs?: number;
  jobHash?: string;
  detailIds?: string[];
  /** The extractor cut a free-text field of the candidate to its length cap. */
  clipped?: true;
}

export const BRAIN_PIPELINE_VERSION = 'brain-extract-v1';
export const BRAIN_EXTRACTION_PROMPT_VERSION = 'extract-v13';
/** Editor token shape: fact:[label](assertionId|teamId). */
export const BRAIN_FACT_TOKEN_PREFIX = 'fact:';
/** KnowledgeAuthorRef AGENT id stamped on Brain system pages. */
export const BRAIN_PROJECTION_AGENT_ID = 'brain';
/** Rough extractions-per-thread ratio used by the backfill estimate until real data exists. */
export const BRAIN_BACKFILL_EXTRACTION_RATIO = 0.6;
export const BRAIN_BACKFILL_ESTIMATE_ROW_CAP = 20_000;
export const BRAIN_ENTITY_MERGE_MAX_HOPS = 5;
export const BRAIN_ENTITY_ROOT_PARENT = 'ROOT';
export const BRAIN_UNLIMITED_DAILY_EXTRACTION_CAP = 0;
export const isBrainDailyCapUnlimited = (cap: number): boolean =>
  cap <= BRAIN_UNLIMITED_DAILY_EXTRACTION_CAP;
export const BRAIN_DEFAULTS = {
  debounceMinutes: 10,
  dailyExtractionCap: BRAIN_UNLIMITED_DAILY_EXTRACTION_CAP,
  reviewMode: BrainReviewMode.AUTO_PUBLISH_CONFIDENT,
  autoAcceptConfidence: 0.75,
} as const satisfies BrainBlueprintProcessing;

export enum BrainDiscoveryRunStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
}

export enum BrainDiscoveryErrorCode {
  TOO_MANY_ATTEMPTS = 'too_many_attempts',
  ENQUEUE_FAILED = 'enqueue_failed',
  PIPELINE_FAILED = 'pipeline_failed',
  NO_MESSAGES = 'no_messages',
  DRAFT_EXISTS = 'draft_exists',
  /** Queued or running past the active window with no finish: the queue message was lost. */
  STALE = 'stale',
}

/** What stopped source collection before every readable message was read. */
export enum BrainDiscoveryTruncation {
  MESSAGE_CAP = 'message_cap',
  TEXT_BUDGET = 'text_budget',
  TIME = 'time',
}

export enum BrainDiscoveryExtraType {
  NONE = 'NONE',
}

export type BrainDiscoveryEntityType =
  | Exclude<BrainEntityType, BrainEntityType.PERSON>
  | BrainDiscoveryExtraType;

export enum BrainDiscoveryAreaSource {
  MODEL = 'model',
  COMMUNITY = 'community',
  TEAM_TITLE_RULE = 'rule_team_title',
  INHERITED = 'inherited_from_parent',
  WORK_EDGES = 'work_edges',
  CUSTOMER_SPREAD = 'customer_spread',
  UNASSIGNED = 'unassigned',
}

export enum BrainDiscoverySignalKind {
  SUBJECT = 'subject',
  BODY = 'body',
  QUOTED = 'quoted',
  INVITE = 'invite',
  CODE = 'code',
  /** Count = the channel's messages in the window. */
  CHANNEL_NAME = 'channel_name',
}

export enum BrainDiscoveryHierarchyField {
  PARENT = 'parent',
  AREA = 'area',
}

export enum BrainDiscoveryConflictReason {
  RULE_OVERRIDE = 'rule_override',
  UNKNOWN_PARENT = 'unknown_parent',
  SELF_PARENT = 'self_parent',
  CYCLE = 'cycle',
  TOO_DEEP = 'too_deep',
  TYPE_RULE = 'type_rule',
  UNKNOWN_AREA = 'unknown_area',
}

/** Who placed the hierarchy: every batch answered, some fell back to rules, or rules only. */
export enum BrainDiscoveryHierarchyMode {
  MODEL = 'model',
  PARTIAL = 'partial',
  FALLBACK = 'fallback',
}

export interface BrainDiscoveryCounts {
  messages: number;
  members: number;
  threads: number;
  areas: number;
  entities: number;
  lowConfidence: number;
  truncated: boolean;
  /** Null when nothing was left behind; absent on runs recorded before it existed. */
  truncatedBy?: BrainDiscoveryTruncation | null;
  /** Members the proposal places in areas or links to names; absent on older runs. */
  peopleCount?: number;
  /** Recurring meetings the run found; absent on older runs. */
  meetings?: number;
  /** Work and ownership edges kept in the proposal; absent on older runs. */
  edgeCount?: number;
  /** Absent on older runs. */
  hierarchyMode?: BrainDiscoveryHierarchyMode;
}

export interface BrainDiscoveryCost {
  haikuCalls: number;
  cacheHits: number;
  /** Totals across models. */
  inputTokens: number;
  outputTokens: number;
  /** Structure-stage (Opus) spend; absent on runs before the split. */
  opusCalls?: number;
  opusInputTokens?: number;
  opusOutputTokens?: number;
}

export interface BrainDiscoveryConfidenceSummary {
  high: number;
  medium: number;
  low: number;
}

/** Run META: every field is an id, enum, count or timestamp, so it stays readable. */
export interface BrainDiscoveryRun {
  teamId: string;
  runId: string;
  status: BrainDiscoveryRunStatus;
  windowDays: number;
  slackChannelIds: string[];
  emailEnabled: boolean;
  /** Whose mailboxes are read; null or absent reads every member's. */
  memberUserIds?: string[] | null;
  /** The starting admin's Google Calendar was read; absent on older runs. */
  calendarEnabled?: boolean;
  startedBy: string;
  attempts: number;
  counts: BrainDiscoveryCounts | null;
  cost: BrainDiscoveryCost | null;
  confidenceSummary: BrainDiscoveryConfidenceSummary | null;
  startedAt: string;
  finishedAt: string | null;
  errorCode: BrainDiscoveryErrorCode | null;
  draftVersion?: number;
  /** When activation started the history load for this run's sources; set once. */
  historyLoadStartedAt?: string;
  /** When the suggested edges of this run were written to the graph; set once. */
  graphAppliedAt?: string;
}

export interface BrainDiscoverySlackChannel {
  id: string;
  name: string;
}

export interface BrainDiscoveryCalendarPerson {
  email: string;
  name: string;
}

/** One recurring event of the starting admin's calendar, as read at start time. */
export interface BrainDiscoveryCalendarSeries {
  seriesId: string;
  title: string;
  organizer: BrainDiscoveryCalendarPerson;
  attendees: BrainDiscoveryCalendarPerson[];
  /** Truncated; never reaches the proposal. */
  description: string;
  /** Start of each occurrence in the window, oldest first. */
  occurrences: string[];
}

/** Sealed beside the run META and deleted once the run ends: what the worker reads besides mail and Slack. */
export interface BrainDiscoveryRunInput {
  slackChannels: BrainDiscoverySlackChannel[];
  calendarSeries: BrainDiscoveryCalendarSeries[];
}

export interface BrainDiscoverySignalCount {
  kind: BrainDiscoverySignalKind;
  count: number;
}

export interface BrainDiscoveryCompactArea {
  id: string;
  name: string;
  source: BrainDiscoveryAreaSource;
  memberCount: number;
  topEntityIds: string[];
}

export interface BrainDiscoveryCompactEntity {
  id: string;
  name: string;
  type: BrainDiscoveryEntityType | null;
  aliases: string[];
  parentId: string | null;
  areaId: string | null;
  areaSource: BrainDiscoveryAreaSource | null;
  areaKeys: string[];
  primaryAreaKey: string | null;
  /** The model's classification confidence; null when the run had no model answer. */
  confidence: number | null;
  score: number;
  signals: BrainDiscoverySignalCount[];
  threadCount: number;
  authorCount: number;
  /** Distinct members whose copies mention the entity; the draft's exposure guard reads it. */
  holderCount: number;
  inPublicChannel: boolean;
  /** Public Slack channels named after the entity; activation links them with lives_in. */
  channelIds?: string[];
}

export interface BrainDiscoveryCompactConflict {
  entityId: string;
  field: BrainDiscoveryHierarchyField;
  reason: BrainDiscoveryConflictReason;
  kept: string | null;
  rejected: string | null;
}

export interface BrainDiscoveryCompactAreaStrength {
  /** The proposal area id, as on BrainDiscoveryCompactArea.id. */
  key: string;
  /** 0 to 1. */
  strength: number;
}

/** A mailbox address the member sent from; the digest only, never the clear address. */
export interface BrainDiscoveryCompactMailbox {
  emailHmac: string;
  /** Display names the address was seen under. */
  names: string[];
}

/** A team member only, never an external address. */
export interface BrainDiscoveryCompactPerson {
  memberUserId: string;
  areaKeys: BrainDiscoveryCompactAreaStrength[];
  /** Messages the member sent or received in the window. */
  messageCount: number;
  /** Addresses other than the login one that this member's own mailbox sent from. */
  mailboxes?: BrainDiscoveryCompactMailbox[];
}

export interface BrainDiscoveryCompactWorkEdge {
  memberUserId: string;
  /** The proposal entity id, as on BrainDiscoveryCompactEntity.id. */
  entityRef: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
  /** 0 to 1. */
  confidence: number;
}

export interface BrainDiscoveryCompactOwnership {
  memberUserId: string;
  entityRef: string;
  evidenceCount: number;
  /** 0 to 1. */
  confidence: number;
}

export interface BrainDiscoveryCompactCoworker {
  /** Id inside the proposal; the draft keeps it as personRef. */
  personRef: string;
  emailHmac: string;
  emailDomain: string;
  displayName: string;
  nameVariants: string[];
  title: string | null;
  titleVariants: string[];
  areaKeys: BrainDiscoveryCompactAreaStrength[];
  stats: BrainCoworkerStats;
  /** personRef of the coworkers this one talks to most. */
  topCorrespondentRefs: string[];
  evidence: BrainCoworkerEvidence[];
  /** Clear address, kept only in the sealed proposal so the admin can open an invite. */
  email?: string;
  /** 1-2 sentence "who they are and what they do"; null when the run had nothing to base one on. */
  summary: string | null;
  /** personRef of the coworker this one reports to, with the confidence and the excerpt behind it. */
  reportsToRef?: string;
  reportsToConfidence?: number;
  reportsToEvidence?: string;
}

/** What the run keeps, sealed: names, member ids and counts — no snippets, sentences or externals. */
export interface BrainDiscoveryCompactProposal {
  runId: string;
  generatedAt: string;
  language: BrainDiscoveryLanguage;
  areas: BrainDiscoveryCompactArea[];
  entities: BrainDiscoveryCompactEntity[];
  conflicts: BrainDiscoveryCompactConflict[];
  /** Absent on proposals written before people were kept. */
  people?: BrainDiscoveryCompactPerson[];
  workEdges?: BrainDiscoveryCompactWorkEdge[];
  ownershipCandidates?: BrainDiscoveryCompactOwnership[];
  /** Absent on proposals written before coworkers were kept. */
  coworkers?: BrainDiscoveryCompactCoworker[];
  /** Proposal entity id → the draft alias entityId it was matched to when the draft was saved. */
  draftEntityIds?: Record<string, string>;
  stats: Record<string, number>;
  /** Absent on proposals written before it existed. */
  hierarchyMode?: BrainDiscoveryHierarchyMode;
  /** Entities cut to keep the sealed row under its size cap. */
  droppedForSize: number;
}

export enum BrainDiscoveryLanguage {
  PT_BR = 'pt-BR',
  EN = 'en',
}

export const BRAIN_DISCOVERY_WINDOW_DAYS = [30, 90] as const;
export const BRAIN_DISCOVERY_DEFAULT_WINDOW_DAYS = 90;
